// Learning Tracks data — sourced from Top_Courses_by_Category.xlsx
// Each category = one worksheet. Add or edit here to update the /learning-tracks page.
import { IMAGES } from "./data";

const deriveLevel = (title) => {
    const t = title.toLowerCase();
    if (/(beginner|introduction|intro |^intro|fundamentals|basics|essentials|foundation|everyone|awareness|core |^core|practical|overview|awareness)/.test(t)) {
        return "Beginner";
    }
    if (/(advanced|expert|professional|master|specialist|architect|designer|lead implementer|associate certified|certified associate|prof(essional)?)/.test(t)) {
        return "Advanced";
    }
    return "Intermediate";
};

export const CATEGORY_META = {
    "GenAI": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/v1s818wf_1.jpg",
        hue: "#A100FF",
        blurb: "Foundation models, RAG, agents & applied Gen AI engineering.",
    },
    "Data, Analytics & AI": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/f0wl5xxm_2.jpg",
        hue: "#3E63DD",
        blurb: "From SQL foundations to ML, big data, and modern analytics.",
    },
    "Cloud & DevOps": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/947w9fo7_3.jpg",
        hue: "#0EA5E9",
        blurb: "Multi-cloud architecture, Kubernetes, IaC, and SRE.",
    },
    "Cybersecurity": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/aa5txsg8_4.jpg",
        hue: "#EF4444",
        blurb: "Defensive & offensive security across cloud, endpoint, and app layers.",
    },
    "Software Engineering": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/x3v8gjbq_5.jpg",
        hue: "#22C55E",
        blurb: "Modern languages, frameworks, and design patterns.",
    },
    "Applications & Platforms": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/mpxyghb5_6.jpg",
        hue: "#F59E0B",
        blurb: "SAP, Salesforce, Microsoft Power Platform, ServiceNow and more.",
    },
    "Testing": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/cvm43nyp_7.jpg",
        hue: "#8B5CF6",
        blurb: "Manual, automation, API, mobile, and performance testing.",
    },
    "Methodologies & Frameworks": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/sfnhspiu_8.jpg",
        hue: "#0891B2",
        blurb: "Agile, Scrum, SAFe, PMP, ITIL, TOGAF, and governance.",
    },
    "Soft-Skills": {
        image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_future-workforce-9/artifacts/a6c0x9mp_9.jpg",
        hue: "#EC4899",
        blurb: "Leadership, communication, and personal effectiveness.",
    },
};

const RAW_CATEGORIES = [
    {
        name: "GenAI",
        courses: [
            "Gen AI for Beginners",
            "Gen AI for Everyone",
            "Gen AI: Introduction and Applications",
            "Gen AI: Prompt Engineering Basics",
            "Gen AI: Foundation Models and Platforms",
            "Introduction to Large Language Models",
            "Fundamentals of AI Agents Using RAG and LangChain",
            "Gen AI with Large Language Models",
            "LangChain for LLM Application Development",
            "Prompt Engineering for ChatGPT",
            "Gen AI for Software Developers",
            "Gen AI for Business Analyst",
            "Gen AI: Impact, Considerations, and Ethical Issues",
            "Gen AI and LLMs: Architecture and Data Preparation",
            "Building AI-Driven Apps: Master GenAI, LangChain, Vector DB, and GPT-4 Integration",
        ],
    },
    {
        name: "Data, Analytics & AI",
        courses: [
            "Data Analysis: A Practical Introduction for Absolute Beginners",
            "Introduction to Machine Learning",
            "Data Visualization with Tableau",
            "Introduction to PySpark",
            "PL-300: Microsoft Power BI Data Analyst",
            "Machine Learning with Python",
            "Natural Language Processing (NLP)",
            "SQL for Data Science",
            "Introduction to Big Data",
            "Big Data Analytics Using Spark",
            "Snowflake SnowPro Core",
            "Deep Learning with Python and PyTorch",
            "Data Science with Python",
            "Databricks Certified Data Engineer Associate",
            "Preparing for the Professional Data Engineer Examination",
        ],
    },
    {
        name: "Cloud & DevOps",
        courses: [
            "Introduction to Google Cloud Platform",
            "AZ-104 Microsoft Azure Administrator Associate",
            "Microsoft Azure Developer Associate (AZ-204)",
            "AWS Certified Cloud Practitioner",
            "AWS Certified Solutions Architect – Associate",
            "AWS Certified DevOps Engineer - Professional",
            "Introduction to Kubernetes",
            "Certified Kubernetes Administrator",
            "Docker and Kubernetes",
            "Infrastructure as code with AWS using Terraform",
            "Introduction to DevOps",
            "Git and GitHub",
            "Ansible",
            "AZ-400 DevOps Engineer Expert",
            "Site Reliability Engineering (SRE) Foundation",
        ],
    },
    {
        name: "Cybersecurity",
        courses: [
            "Cyber Security: Awareness Training For Everyone",
            "CompTIA Security+",
            "Certified Information Systems Security Professional (CISSP)",
            "Certified Information Security Manager (CISM)",
            "Certified Ethical Hacking Essentials",
            "Certified Information Systems Auditor (CISA)",
            "OSCP (Offensive Security Certified Professional)",
            "SC-100T00: Microsoft Cybersecurity Architect",
            "CompTIA PenTest+",
            "Web Application Security Testing",
            "Digital Forensics Essentials",
            "Certified SOC Analyst",
            "DevSecOps Foundation",
            "Check Point Certified Security Administrator (CCSA)",
            "Network Security Fundamentals",
        ],
    },
    {
        name: "Software Engineering",
        courses: [
            "Java Fundamentals",
            "Core Java",
            "Python Fundamentals",
            "Advanced Python",
            "C# Programming",
            "C++ Programming",
            "JavaScript Introduction",
            "React JS",
            "Angular 20",
            "Node.js / Express JS",
            "Spring Framework / Spring Boot",
            "Data Structures and Algorithms using C++",
            "Design Patterns in Java",
            "Golang",
            "Flutter Application Development",
        ],
    },
    {
        name: "Applications & Platforms",
        courses: [
            "SAP S/4HANA (Overview)",
            "SAP Financial Accounting and Controlling (FI&CO)",
            "Introduction to SAP HCM (Human Capital Management)",
            "Salesforce Certified Administrator (SU 20)",
            "Salesforce Developer",
            "Customer Relationship Management (CRM)",
            "Microsoft Power Platform",
            "Microsoft Power Apps",
            "Power Automate",
            "MS Dynamics 365 Fundamentals",
            "ERP (Enterprise Resource Planning)",
            "Introduction to ServiceNow",
            "MuleSoft Certified Developer - Integration and API Associate",
            "Introduction to SQL",
        ],
    },
    {
        name: "Testing",
        courses: [
            "Introduction to Software Testing",
            "ISTQB - Tester Foundation Level",
            "Selenium",
            "Selenium - Cucumber",
            "Manual Testing",
            "Automation Testing",
            "API Testing",
            "POSTMAN",
            "Jmeter",
            "Appium",
            "Cypress Automation Testing Framework",
            "Software Testing Life Cycle (STLC)",
            "Mobile Testing",
            "Tricentis Tosca Automation Tool",
            "Certified Software Tester (CSTE)",
        ],
    },
    {
        name: "Methodologies & Frameworks",
        courses: [
            "Agile Project Management",
            "Agile Methodologies",
            "PRINCE2",
            "Certified Scrum Master (CSM)",
            "Certified Scrum Product Owner (CSPO)",
            "SAFe (Scaled Agile Framework)",
            "Leading SAFe Agile (SA)",
            "Project Management Professional (PMP)",
            "ITIL Foundation V4",
            "TOGAF",
            "Six Sigma / Six Sigma Green Belt",
            "Change Management Foundation and Practitioner",
            "Kanban",
            "PECB Certified ISO/IEC 27001 Lead Implementer",
            "Introduction to Jira",
        ],
    },
    {
        name: "Soft-Skills",
        courses: [
            "Developing Personal Effectiveness and Teamwork",
            "Fostering Psychological Safety & Belonging on Teams",
            "Creative and Lateral Thinking",
            "Talent Development for Leaders: Develop Your High-Potentials",
            "Feedback is Fuel",
            "Motivating Your Team: How to Keep Morale High",
            "Communication Excellence",
            "Operational Excellence",
            "Service Excellence",
            "Stakeholder Management",
            "Artificial Intelligence for Leaders",
            "Gen AI for Leadership",
        ],
    },
];

const slugify = (s) =>
    s.toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

export const LEARNING_CATEGORIES = RAW_CATEGORIES.map((cat) => {
    const meta = CATEGORY_META[cat.name] || {};
    return {
        name: cat.name,
        slug: slugify(cat.name),
        image: meta.image || IMAGES.aiAbstract,
        hue: meta.hue || "#A100FF",
        blurb: meta.blurb || "",
        courses: cat.courses.map((title, i) => ({
            id: `${slugify(cat.name)}-${i + 1}`,
            title,
            level: deriveLevel(title),
        })),
    };
});

export const ALL_COURSES = LEARNING_CATEGORIES.flatMap((c) =>
    c.courses.map((course) => ({ ...course, category: c.name, categorySlug: c.slug, image: c.image, hue: c.hue }))
);
